import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileX,
  FileText,
} from "lucide-react";
import React, { useCallback } from "react";
import moment from "moment";
import { Group, Tooltip, ActionIcon, Text } from "@mantine/core";

import { LeaveExportsListProps, LeaveExport } from "../utils/types";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";
import DirectoryTable from "../../../themes/components/DirectoryTable";

const LeaveExportsList: React.FC<LeaveExportsListProps> = ({
  page,
  pages,
  exports = [],
  exportCount,
  setPage,
}) => {
  const getPaginationDisplay = useCallback((): string => {
    if (exports.length === 0) {
      return "0 of 0";
    }

    const startIndex = (page - 1) * 10 + 1;
    const endIndex = Math.min(page * 10, exportCount);

    return `${startIndex} - ${endIndex} of ${exportCount}`;
  }, [page, exportCount, exports]);

  const renderEmptyExportsList = (): JSX.Element => (
    <div className="flex flex-1 flex-col items-center justify-center h-5/6 border border-dashed rounded border-jem-primary-20t min-h-0">
      <FileText className="w-[40px] h-[40px] text-[#829099]" />
      <p
        className="mt-3 text-jem-primary-base"
        style={{
          fontFamily: "Inter",
          fontWeight: 700,
          fontSize: "20px",
          lineHeight: "100%",
          letterSpacing: "0.16px",
          textAlign: "center",
        }}
      >
        No exports available for download
      </p>
      <p
        className="mt-3 text-jem-primary-base"
        style={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "20px",
          letterSpacing: "0px",
          textAlign: "center",
        }}
      >
        Click on “Create Export” to export a CSV file for download.
      </p>
    </div>
  );

  const tableMaxHeight = ResponsiveHeight(
    "",
    "80vh",
    "80vh",
    "80vh",
    "75vh",
    "80vh"
  );
  const headers = ["Export batch Name", "Date created", "Actions"];
  const renderExportsList = useCallback((): JSX.Element => {
    const rows = exports?.map((job: LeaveExport) => ({
      jobName: (
        <Group spacing="sm">
          <Text size="sm">{job.job_name}</Text>
        </Group>
      ),
      dateCreated: (
        <Group spacing="sm">
          <Text size="sm">
            {moment(job.date_created).format("DD/MM/YYYY HH:mm")}
          </Text>
        </Group>
      ),
      actions: job.export_file_path ? (
        <Tooltip label="Download Exports CSV" withArrow>
          <ActionIcon
            component="a"
            target="_blank"
            rel="noopener noreferrer"
            href={job.export_file_path}
            aria-label="Download Export CSV"
          >
            <Download size={20} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <ActionIcon disabled aria-label="Download Exports CSV Unavailable">
          <Tooltip
            label="CSV is not ready for this job; please refresh the page."
            withArrow
          >
            <FileX size={20} />
          </Tooltip>
        </ActionIcon>
      ),
    }));

    return (
      <>
        {/* Exports List table */}
        <DirectoryTable
          rows={rows}
          headers={headers}
          tableMaxHeight={tableMaxHeight}
          data={exports?.map((e, index) => ({ ...e, id: Number(index) }))}
          hideSelectControls
          selection={[] as never[]}
          onSelect={() => undefined}
        />

        {/* Pagination Controls */}
        {exportCount !== undefined && exportCount > 0 && (
          <div className="sticky bottom-0 z-10 flex flex-row items-center justify-end gap-2 p-4 bg-white border-t border-jem-shade">
            <span className="text-jem-primary-50t">
              {getPaginationDisplay()}
            </span>
            <ActionIcon
              disabled={page === 1 || exportCount === 0}
              onClick={() => setPage(page - 1)}
              aria-label="Previous Page"
              className="bg-transparent"
            >
              <ChevronLeft size={20} />
            </ActionIcon>
            <ActionIcon
              disabled={page === pages || exportCount === 0}
              onClick={() => setPage(page + 1)}
              aria-label="Next Page"
              className="bg-transparent"
            >
              <ChevronRight size={20} />
            </ActionIcon>
          </div>
        )}
      </>
    );
  }, [exports, page, pages, exportCount, setPage]);

  return (
    <div className="flex-1 h-full">
      <div className="flex-1 h-full">
        {exports.length === 0 ? renderEmptyExportsList() : renderExportsList()}
      </div>
    </div>
  );
};

export default LeaveExportsList;
